import ReplayIcon from '@mui/icons-material/Replay';
import type { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useQueryClient } from '@tanstack/react-query';
import type { MouseEvent } from 'react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import type { SilobagRow } from '@siloreal:modules:private/silobag-list/config/extra/types';
import { restoreArchivedSilobagService } from '@siloreal:shared:api/service/silobag/silobag-archived-restore';
import * as UI from '@siloreal:shared:ui';
import { useSilobagListContext } from '../../../context';
import * as HOOKS from '@siloreal:shared:hooks';

const ARCHIVED_LIST_QUERY_KEY = ['silobag-archived-list'] as const;

type RestoreActionProps = {
  actionId: string;
  disabled?: boolean;
};

type RestoreSilobag = {
  id: string;
  name: string;
  writePermission: boolean;
};

type RestoreModalState = {
  restorable: RestoreSilobag[];
  blocked: RestoreSilobag[];
} | null;

type ArchivedCurrentRow = {
  id: string | number;
  silobag_name?: string;
  name?: string;
  write_permission?: boolean;
};

function getSelectedIds(selectionModel: GridRowSelectionModel): string[] {
  return Array.from(selectionModel.ids).map(String);
}

function toRestoreSilobag(row: ArchivedCurrentRow): RestoreSilobag {
  const id = String(row.id);

  return {
    id,
    name: row.name ?? row.silobag_name ?? id,
    writePermission: row.write_permission === true,
  };
}

function useRestoreSilobags() {
  const queryClient = useQueryClient();
  const {
    async: {
      list: { current },
    },
  } = useSilobagListContext();
  const [restoreModal, setRestoreModal] = useState<RestoreModalState>(null);
  const [isRestoring, setIsRestoring] = useState(false);

  const currentRows = (current?.data ?? []) as unknown as ArchivedCurrentRow[];

  const openRestoreModal = (ids: string[], selectedRows: ArchivedCurrentRow[] = []): void => {
    if (ids.length === 0) return;

    const currentRowById = new Map(currentRows.map((row) => [String(row.id), row]));
    const selectedRowById = new Map(selectedRows.map((row) => [String(row.id), row]));
    const silobags = ids.map((id) => {
      const row = selectedRowById.get(id) ?? currentRowById.get(id);

      return toRestoreSilobag(row ?? { id, write_permission: false });
    });

    setRestoreModal({
      restorable: silobags.filter((silobag) => silobag.writePermission),
      blocked: silobags.filter((silobag) => !silobag.writePermission),
    });
  };

  const closeRestoreModal = (): void => {
    if (isRestoring) return;

    setRestoreModal(null);
  };

  const i18n = HOOKS.useLocale.getState();

  const restoreSilobags = async (): Promise<void> => {
    if (!restoreModal || restoreModal.restorable.length === 0) return;

    setIsRestoring(true);

    try {
      await Promise.all(
        restoreModal.restorable.map((silobag) => restoreArchivedSilobagService({ query: silobag.id }))
      );
      await queryClient.invalidateQueries({ queryKey: ARCHIVED_LIST_QUERY_KEY });

      toast.success(
        `${restoreModal.restorable.map((silobag) => silobag.id).join(', ')} restauradas correctamente.`
      );
      setRestoreModal(null);
    } catch {
      toast.error(i18n.t('temp.common.toasts.error.restore_silobags'));
    } finally {
      setIsRestoring(false);
    }
  };

  const modal = restoreModal ? (
    <UI.Modal
      open
      onClose={closeRestoreModal}
      title="Restaurar silobolsas"
      description="Confirmá la restauración de las silobolsas seleccionadas."
      disableBackdropClose={isRestoring}
      actions={
        <>
          <UI.Button
            variant="alternative-hollow"
            uppercase={false}
            disabled={isRestoring}
            onClick={closeRestoreModal}
          >
            Cancelar
          </UI.Button>
          <UI.Button
            variant="primary"
            uppercase={false}
            loading={isRestoring}
            disabled={restoreModal.restorable.length === 0}
            onClick={() => void restoreSilobags()}
            icon={{ value: <ReplayIcon fontSize="small" />, position: 'end' }}
            tid="silobag-list-confirm-restore-btn"
          >
            Restaurar
          </UI.Button>
        </>
      }
      tid="silobag-list-restore-modal"
    >
      {restoreModal.blocked.length > 0 ? (
        <p>
          No tenés permisos para restaurar: {restoreModal.blocked.map((silobag) => silobag.id).join(', ')}
        </p>
      ) : null}

      {restoreModal.restorable.length > 0 ? (
        <p>Se restaurarán: {restoreModal.restorable.map((silobag) => silobag.id).join(', ')}</p>
      ) : (
        <p>No hay silobolsas disponibles para restaurar.</p>
      )}
    </UI.Modal>
  ) : null;

  return {
    isRestoring,
    modal,
    openRestoreModal,
  };
}

export function RestoreAction(props: RestoreActionProps) {
  const { actionId, disabled } = props;
  const explorer = UI.collection.useExplorer();
  const { isRestoring, modal, openRestoreModal } = useRestoreSilobags();

  const handleSelectionConfirm = (selectionModel: GridRowSelectionModel): void => {
    openRestoreModal(getSelectedIds(selectionModel));
  };

  const handleClick = (): void => {
    explorer.startSelection({
      id: actionId,
      onConfirm: handleSelectionConfirm,
    });
  };

  const i18n = HOOKS.useLocale();
  const label = i18n.t('temp.private.list.silobag.restore');

  return (
    <>
      <UI.Tooltip content={label} tid={`${actionId}-tooltip`}>
        <UI.Button
          variant="minor"
          uppercase={false}
          ariaLabel={label}
          icon={<ReplayIcon fontSize="small" />}
          onClick={handleClick}
          loading={isRestoring}
          disabled={isRestoring || disabled}
          tid={`${actionId}-btn`}
        />
      </UI.Tooltip>

      {modal}
    </>
  );
}

type RestoreRowActionProps = {
  row: SilobagRow;
};

export function RestoreRowAction(props: RestoreRowActionProps) {
  const { row } = props;
  const { isRestoring, modal, openRestoreModal } = useRestoreSilobags();

  const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    openRestoreModal([String(row.id)], [row as unknown as ArchivedCurrentRow]);
  };

  return (
    <>
      <UI.Button
        variant="alternative-hollow"
        removeBorder
        icon={{ value: <ReplayIcon fontSize="small" />, only: true }}
        ariaLabel="Restaurar silobolsa"
        onClick={handleClick}
        loading={isRestoring}
        disabled={isRestoring}
        tid="silobag-list-row-restore-btn"
      />

      {modal}
    </>
  );
}
